import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskLines, Reveal, EASE } from "../components/motion";
import { SectionHeading } from "../components/SectionHeading";
import { STATS, VALUES, PROJECTS_UNDER_CONSTRUCTION, PROJECTS_COMPLETED, COMPANY } from "../lib/content";
import { SERVICE_LIST } from "../lib/services";
import { Seo } from "../components/Seo";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} className="relative w-full overflow-hidden bg-navy h-screen min-h-[600px] flex items-center" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          poster="/media/hero-poster.jpg"
          data-testid="hero-video"
        >
          <source src="/media/hero.mp4" type="video/mp4" />
        </video>
      </motion.div>

      {/* Navy gradient overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/55 to-navy/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/30" />

      <div className="relative w-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col justify-start md:justify-center pt-28 pb-12 md:pt-24 md:pb-0">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="eyebrow mb-4 md:mb-5"
        >
          Southern California General Contractor
        </motion.p>

        <h1 className="font-serif font-light text-white text-[2.4rem] leading-[1.02] sm:text-5xl md:text-6xl lg:text-[4.2rem] tracking-tight max-w-4xl">
          <MaskLines
            lines={["Build Your Horizon with", "Next Horizon Builders."]}
            delay={0.3}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-6 text-white/75 max-w-xl text-sm md:text-base leading-relaxed font-light"
        >
          {COMPANY.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="mt-9 flex flex-wrap gap-4"
        >
          <Link
            to="/projects"
            data-testid="hero-explore-projects-btn"
            className="group inline-flex items-center gap-3 bg-gold text-white px-8 py-4 text-[0.72rem] font-semibold uppercase tracking-[0.2em] hover:bg-gold-dark transition-colors duration-300"
          >
            Explore Projects
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-contact-btn"
            className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 text-[0.72rem] font-semibold uppercase tracking-[0.2em] hover:bg-white hover:text-navy transition-all duration-300"
          >
            Contact Us
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const StatsStrip = () => (
  <section className="bg-white border-b border-gray-100" data-testid="stats-strip">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <div className={`py-8 md:py-12 ${i !== 0 ? "lg:border-l border-gray-100 lg:pl-10" : ""}`}>
            <div className="font-serif font-light text-2xl md:text-4xl text-navy">{s.value}</div>
            <div className="mt-3 text-[0.7rem] uppercase tracking-[0.2em] text-darkgray/50 font-medium">{s.label}</div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Manifesto = () => (
  <section className="bg-white py-24 md:py-36" data-testid="manifesto-section">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <SectionHeading
              eyebrow="Our Standard"
              title={<>Built on four <span className="italic text-gold">principles.</span></>}
              subtitle="For over fifteen years we've delivered ground-up multifamily communities across Southern California — guided by a discipline that never bends."
            />
          </div>
        </div>
        <div className="lg:col-span-8">
          <div className="divide-y divide-gray-100 border-t border-gray-100">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.05}>
                <div className="group grid md:grid-cols-12 gap-6 py-10 md:py-12 items-start hover:bg-lightgray/50 transition-colors duration-500 px-2 md:px-6">
                  <div className="md:col-span-2 font-serif text-3xl text-gold/70">{v.n}</div>
                  <h3 className="md:col-span-4 font-serif font-light text-3xl md:text-4xl text-navy tracking-tight">{v.title}</h3>
                  <p className="md:col-span-6 text-darkgray/70 leading-relaxed font-light">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ServicesPreview = () => (
  <section className="bg-lightgray py-24 md:py-36" data-testid="services-preview">
    <div className="max-w-[1440px] mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
        <SectionHeading
          eyebrow="What We Do"
          title={<>Capabilities that carry<br />a project end to end.</>}
        />
        <Reveal delay={0.1}>
          <Link to="/services" className="group inline-flex items-center gap-3 text-navy text-xs font-semibold uppercase tracking-[0.2em]">
            All Services
            <ArrowUpRight size={18} className="text-gold group-hover:rotate-45 transition-transform duration-300" />
          </Link>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200">
        {SERVICE_LIST.map((s, i) => (
          <Reveal key={s.slug} delay={(i % 4) * 0.08}>
            <Link to={`/services/${s.slug}`} className="group block bg-white p-9 md:p-11 h-full hover:bg-navy transition-colors duration-500">
              <div className="flex items-center justify-between mb-14">
                <span className="font-serif text-2xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                <ArrowUpRight size={20} className="text-gray-300 group-hover:text-gold group-hover:rotate-45 transition-all duration-300" />
              </div>
              <h3 className="font-serif font-light text-2xl md:text-3xl text-navy group-hover:text-white transition-colors duration-500 tracking-tight">{s.name}</h3>
              <p className="mt-4 text-sm text-darkgray/60 group-hover:text-white/60 leading-relaxed transition-colors duration-500 font-light">{s.short}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const EditorialMarquee = () => (
  <section className="bg-navy py-14 md:py-20 overflow-hidden" data-testid="editorial-marquee">
    <Marquee speed={38} gradient={false} autoFill>
      {["Precision", "Integrity", "Safety", "Long-Term Value"].map((w, i) => (
        <span key={i} className="inline-flex items-center">
          <span className="font-serif font-light italic text-white/90 text-5xl md:text-7xl px-10">{w}</span>
          <span className="text-gold text-3xl md:text-5xl">✦</span>
        </span>
      ))}
    </Marquee>
  </section>
);

const ProjectsPreview = () => {
  const featured = [...PROJECTS_UNDER_CONSTRUCTION, ...PROJECTS_COMPLETED].slice(0, 3);
  return (
    <section className="bg-white py-24 md:py-36" data-testid="projects-preview">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <SectionHeading
            eyebrow="Selected Work"
            title={<>Communities we've<br />brought to life.</>}
          />
          <Reveal delay={0.1}>
            <Link to="/projects" className="group inline-flex items-center gap-3 text-navy text-xs font-semibold uppercase tracking-[0.2em]">
              View All Projects
              <ArrowUpRight size={18} className="text-gold group-hover:rotate-45 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {featured.map((pr, i) => (
            <Reveal key={pr.title} delay={i * 0.1}>
              <Link to="/projects" className="group block relative overflow-hidden">
                <div className="relative overflow-hidden aspect-[4/5]">
                  <img
                    src={pr.images[0]}
                    alt={pr.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/15 to-transparent opacity-85 group-hover:opacity-95 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 p-8">
                    <p className="text-gold text-[0.68rem] uppercase tracking-[0.2em] mb-2">{pr.type} · {pr.status}</p>
                    <h3 className="font-serif font-light text-3xl text-white tracking-tight">{pr.title}</h3>
                    <p className="text-white/70 text-sm mt-1">{pr.location}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutTeaser = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} className="bg-lightgray py-24 md:py-36 overflow-hidden" data-testid="about-teaser">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
        <div className="relative aspect-[4/5] overflow-hidden order-2 lg:order-1">
          <motion.img
            style={{ y }}
            src="/media/generated/home-grinder.jpeg"
            alt="Precision on site"
            loading="lazy"
            className="absolute inset-0 w-full h-[116%] object-cover"
          />
          <div className="absolute -bottom-px -right-px bg-navy text-white p-8 md:p-10 max-w-xs">
            <div className="font-serif font-light text-5xl text-gold">{COMPANY.founded}</div>
            <p className="mt-2 text-sm text-white/70 uppercase tracking-[0.15em]">Building California since</p>
          </div>
        </div>

        <div className="order-1 lg:order-2">
          <SectionHeading
            eyebrow="Who We Are"
            title={<>A builder measured by<br />what stands the test<br />of time.</>}
            subtitle="Next Horizon Builders is a Southern California general contractor specializing in ground-up multifamily construction. We combine self-perform capability, disciplined preconstruction, and an uncompromising safety culture to deliver assets owners are proud to hold for generations."
          />
          <Reveal delay={0.2}>
            <Link
              to="/about"
              className="mt-9 inline-flex items-center gap-3 border border-navy text-navy px-8 py-4 text-[0.72rem] font-semibold uppercase tracking-[0.2em] hover:bg-navy hover:text-white transition-all duration-300"
            >
              Our Story
              <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Seo
        title="Next Horizon Builders | Southern California General Contractor"
        description="Next Horizon Builders is a Southern California general contractor specializing in ground-up multifamily construction, project management, preconstruction, and design-build coordination."
        path="/"
        image="/media/hero-poster.jpg"
      />
      <Hero />
      <StatsStrip />
      <Manifesto />
      <ServicesPreview />
      <EditorialMarquee />
      <ProjectsPreview />
      <AboutTeaser />
    </>
  );
}
